<script setup lang="ts">
// Progressive details screen. Opens immediately after a plate locks; each
// data source of the lookup session streams into its own card as it lands:
// soov /car-data (~600 ms) fills the hero + basic data, /car-tax the tax
// card, /auto24-price the price banner (~20 s), /mnt the registry backbone
// (~5 s), and the AI summary follows /car-data. Nothing here blocks.
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { MntResponse } from '../api/checkVehicle';
import { extractCarTax } from '../api/carTax';
import type { VehicleLookupSession } from '../composables/useVehicleLookup';
import type { LatvianRegCheckResponse } from '../api/latvianRegCheck';
import MntDetailSections from '../components/MntDetailSections.vue';
import QuickIcon from '../components/QuickIcon.vue';
import HistoryPanel from '../components/HistoryPanel.vue';
import ManualLookupForm from '../components/ManualLookupForm.vue';
import { lockBodyScroll, unlockBodyScroll } from '../composables/useScrollLock';
import { scrollToSection } from '../composables/useSectionPulse';
import {
  countryFlag,
  dash,
  deriveOwnerStats,
  formatCc,
  formatKg,
  formatKm,
  formatKw,
  formatLitersFromCc,
  formatMoney,
  projectMileageKm,
} from '../vehicle/format';
import {
  formatStaticValue,
  splitPriceRange,
  translateBodyType,
  translateColor,
  translateFuelType,
  translateTransmission,
} from '../vehicle/soov';
import {
  mntFieldTranslation,
  translateCategory,
  translateDriveType,
  translateGenericValue,
  translateRegistrationFee,
  translateMntValue,
} from '../vehicle/mntTranslations';
import { formatEuro, localizeUnitText } from '../vehicle/units';

interface DetailRow {
  label: string;
  value: string;
  tone?: 'good' | 'bad' | 'warn' | 'muted';
}

const props = defineProps<{ session: VehicleLookupSession }>();
const emit = defineEmits<{ back: []; lookup: [payload: { plate: string; region?: string }] }>();

const { t, locale } = useI18n();

// Floating quick-access: history/favorites list and a text/photo plate search,
// both re-open the details screen for the chosen plate (served from cache when
// fresh — see useVehicleLookup).
const showHistory = ref(false);
const showSearch = ref(false);
const aiExpanded = ref(false);

// The search sheet is inline markup (not HistoryPanel, which locks itself):
// freeze the page behind it the same way while it is open.
watch(showSearch, (open) => (open ? lockBodyScroll() : unlockBodyScroll()));
onBeforeUnmount(() => {
  if (showSearch.value) unlockBodyScroll();
  if (priceCaptionTimer !== undefined) window.clearTimeout(priceCaptionTimer);
});

// The price card shows bare digits (icon + range, no label); a tap answers
// "what is this?" by revealing the label for a few seconds and pulsing the
// card. There is no deeper price section to scroll to.
const priceCaptionVisible = ref(false);
let priceCaptionTimer: number | undefined;
function revealPriceCaption(event: Event): void {
  priceCaptionVisible.value = true;
  if (priceCaptionTimer !== undefined) window.clearTimeout(priceCaptionTimer);
  priceCaptionTimer = window.setTimeout(() => (priceCaptionVisible.value = false), 4000);
  scrollToSection([], event);
}

function openLookup(plate: string, region?: string): void {
  showSearch.value = false;
  showHistory.value = false;
  emit('lookup', { plate, ...(region ? { region } : {}) });
}
function openFromHistory(payload: { plate: string; region?: string }): void {
  showHistory.value = false;
  emit('lookup', payload);
}
const latvianShortcut = computed(() => props.session.isLatvian);
const latvianData = computed<LatvianRegCheckResponse | null>(() =>
  props.session.latvian.status === 'success' ? props.session.latvian.data : null,
);
const latvianPending = computed(() => props.session.latvian.status === 'loading');
const latvianError = computed(() => props.session.latvian.status === 'error');

const mntResponse = computed<MntResponse | null>(() =>
  props.session.mnt.status === 'success' ? props.session.mnt.data : null,
);
const mntNotFound = computed(() => mntResponse.value?.status === 'not_found');
const vehicle = computed(() => (mntResponse.value && !mntNotFound.value ? mntResponse.value.vehicle : null));
const mntPending = computed(() => props.session.mnt.status === 'loading');

const dyn = computed(() => props.session.carData.data?.dynamic ?? null);
const stat = computed(() => props.session.carData.data?.static ?? null);

const registrationPlate = computed(
  () =>
    latvianData.value?.reg_nr ??
    vehicle.value?.identification.registration_number ??
    mntResponse.value?.regnr ??
    props.session.plate,
);

// First number in the string ("1968cm3" → 1968); a plain \D strip would
// swallow the digit inside the unit.
function digits(value: string | null | undefined): number | null {
  const match = value?.match(/\d[\d\s]*/)?.[0]?.replace(/\s/g, '') ?? '';
  return match.length > 0 ? Number(match) : null;
}

const vehicleYear = computed(() => {
  const summary = vehicle.value?.summary;
  return (
    summary?.first_registration.iso?.slice(0, 4) ??
    summary?.first_registration.raw?.match(/\b\d{4}\b/)?.[0] ??
    dyn.value?.registreerimisaasta?.trim() ??
    null
  );
});
const enginePowerKw = computed(
  () => vehicle.value?.summary.engine_power_kw ?? vehicle.value?.technical_details.engine.power_kw ?? null,
);
// soov voimsus is bare kW digits ("265"), unlike the guide's "110 kW" example
const enginePower = computed(() =>
  formatKw(enginePowerKw.value ?? digits(dyn.value?.voimsus), locale.value),
);
const engineDisplacementCc = computed(
  () =>
    vehicle.value?.summary.engine_displacement_cc ??
    vehicle.value?.technical_details.engine.displacement_cc ??
    digits(stat.value?.MOOTORI_MAHT?.value),
);
const engineDisplacement = computed(() => formatLitersFromCc(engineDisplacementCc.value, locale.value));
// Quick card shows both units ("2 L / 1 968 cc"), like the registry section.
const engineDisplacementLabel = computed(() => {
  const cc = engineDisplacementCc.value;
  if (cc === null || cc === undefined || Number.isNaN(cc)) return engineDisplacement.value;
  return `${engineDisplacement.value} / ${formatCc(cc, locale.value)}`;
});
const kerbWeight = computed(() =>
  formatKg(
    vehicle.value?.technical_details.masses.kerb_weight_kg ?? digits(stat.value?.TYHIMASS?.value),
    locale.value,
  ),
);

const title = computed(() => {
  if (vehicle.value) {
    const power = enginePower.value === '-' ? null : enginePower.value;
    const parts = [vehicleYear.value, vehicle.value.identification.make_model, power].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  if (dyn.value?.make || dyn.value?.model) {
    const power = enginePower.value === '-' ? null : enginePower.value;
    const parts = [dyn.value.registreerimisaasta?.trim(), dyn.value.make?.trim(), dyn.value.model?.trim(), power];
    const present = parts.filter((part): part is string => Boolean(part));
    if (present.length > 0) return present.join(' ');
  }
  if (latvianData.value) {
    const parts = [latvianData.value.make?.trim(), latvianData.value.model?.trim()].filter(
      (part): part is string => Boolean(part),
    );
    if (parts.length > 0) return parts.join(' ');
    if (latvianData.value.make_model?.trim()) return latvianData.value.make_model.trim();
  }
  return registrationPlate.value;
});

// ---- quick status card (mnt-backed) ----

const ownerStats = computed(() => deriveOwnerStats(vehicle.value?.operations_history));
const isCorporate = computed(() => {
  if (!vehicle.value) return null;
  return vehicle.value.status_messages.some((message) => {
    const text = message.text?.toLowerCase() ?? '';
    return text.includes('töösõiduk') || text.includes('toosoiduk');
  });
});
const ownerTypeLabel = computed(() => {
  if (isCorporate.value === null) return mntPending.value ? '…' : '-';
  return isCorporate.value ? t('details.values.ownerCompany') : t('details.values.ownerPrivate');
});
const mileageSummary = computed(() => {
  if (!vehicle.value) return mntPending.value ? '…' : '-';
  const summary = vehicle.value.mileage.summary;
  const projected = projectMileageKm(summary.latest_km, summary.last_reported_at?.iso);
  if (projected !== null) return formatKm(projected, locale.value);
  // No odometer reading — estimate from first registration at 15 000 km/year
  // (proportional, rounded up to the next full 1000 km).
  const estimated = projectMileageKm(0, firstRegistrationIso.value);
  return estimated === null ? '-' : formatKm(estimated, locale.value);
});
const inspectionTone = computed(() => {
  const passed = vehicle.value?.inspections.current_passed;
  if (passed === true) return 'good';
  if (passed === false) return 'bad';
  return 'muted';
});
const inspectionDate = computed(() => {
  if (!vehicle.value) return mntPending.value ? '…' : '-';
  const date = vehicle.value.inspections.current_valid_until ?? vehicle.value.inspections.latest_inspection_date;
  return dash(date?.raw ?? date?.iso ?? null);
});
const pledge = computed(() => vehicle.value?.restrictions.register_pledge ?? null);
const pledgeTone = computed(() => {
  if (!pledge.value) return 'muted';
  return pledge.value.has_entries ? 'bad' : 'good';
});
const pledgeSummary = computed(() => {
  if (!pledge.value) return mntPending.value ? '…' : '-';
  if (!pledge.value.has_entries) return t('details.values.noPledge');
  const amount = formatMoney(pledge.value.entries[0]?.amount ?? null, locale.value);
  return amount !== '-' ? amount : t('details.values.hasPledge');
});
const currentCountryFlag = computed(() => {
  if (!vehicle.value) return mntPending.value ? '…' : '-';
  const registration = vehicle.value.technical_details.registration;
  return countryFlag(registration.country_of_origin ?? registration.origin_raw);
});
const firstRegistrationIso = computed(
  () =>
    vehicle.value?.summary.first_registration.iso ??
    vehicle.value?.technical_details.registration.first_registration.iso ??
    null,
);
const currentOwnerDuration = computed(() => {
  if (!vehicle.value) return mntPending.value ? '…' : '-';
  // No recorded owner change → still with the first owner, so measure from
  // first registration.
  return formatLocalizedOwnerDuration(ownerStats.value.currentOwnerSince ?? firstRegistrationIso.value);
});
const currentOwnerCount = computed(() => {
  if (!vehicle.value) return mntPending.value ? '…' : '-';
  return String(ownerStats.value.estimatedOwnerCount ?? 1);
});

function monthsSince(startIso: string, end = new Date()): number {
  const start = new Date(`${startIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return 0;
  let months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12;
  months += end.getUTCMonth() - start.getUTCMonth();
  if (end.getUTCDate() < start.getUTCDate()) months -= 1;
  return Math.max(0, months);
}

function durationPart(count: number, unit: 'year' | 'month'): string {
  const key = count === 1 ? unit : `${unit}s`;
  return `${count} ${t(`details.duration.${key}`)}`;
}

function formatLocalizedOwnerDuration(startIso: string | null): string {
  if (!startIso) return '-';
  const totalMonths = monthsSince(startIso);
  if (totalMonths < 1) return t('details.duration.lessThanMonth');

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  if (years > 0 && months > 0) {
    return `${durationPart(years, 'year')} ${durationPart(months, 'month')}`;
  }
  if (years > 0) return durationPart(years, 'year');
  return durationPart(months, 'month');
}

// ---- price banner (auto24) ----

const priceRange = computed(() =>
  props.session.price.status === 'success'
    ? splitPriceRange(props.session.price.data?.average_price, t('details.price.separator'))
    : null,
);

// ---- performance quick card (mnt + car-data + AI) ----

const acceleration = computed(() => {
  if (latvianShortcut.value) return '-';
  if (props.session.ai.status === 'success' && props.session.ai.data?.zeroTo100Seconds) {
    return localizeUnitText(`${props.session.ai.data.zeroTo100Seconds} s`, locale.value) ?? '-';
  }
  return props.session.ai.status === 'loading' ? '…' : '-';
});
// 100-200 km/h rides along in small print under the 0-100 figure; hidden
// entirely when the model returned null (car can't realistically do it).
const acceleration100200 = computed(() => {
  if (latvianShortcut.value) return null;
  const value = props.session.ai.status === 'success' ? props.session.ai.data?.hundredTo200Seconds : null;
  return value ? localizeUnitText(`${value} s`, locale.value) : null;
});

// Fuel type belongs next to the engine figures, not buried mid-list in Basic
// Data — shown in the power cell of the performance quick card.
const fuelTypeLabel = computed(() => {
  const raw =
    vehicle.value?.summary.fuel ??
    vehicle.value?.technical_details.engine.fuel ??
    dyn.value?.kutuse_tuup ??
    null;
  return raw ? translateFuelType(raw, locale.value) : null;
});

function staticFuel(key: string): string | null {
  return formatStaticValue(stat.value?.[key]?.value, locale.value);
}
// AI-estimated fuel economy fills the gap when neither the registry (mnt) nor
// soov car-data reports a figure (fetched in realtime by useVehicleLookup once
// both sources settle). The model returns a bare number ("6.5"); attach the
// unit so it reads like the registry figures.
const aiFuelFigures = computed(() =>
  props.session.aiFuel.status === 'success' ? props.session.aiFuel.data : null,
);
const aiFuelPending = computed(() => props.session.aiFuel.status === 'loading');
function estimatedFuel(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed && /\d/.test(trimmed) ? `${trimmed} l/100km` : null;
}
function fuelDisplay(known: string | null | undefined, estimate: string | null | undefined): string {
  const value = known ?? estimatedFuel(estimate);
  if (!value && aiFuelPending.value) return '…';
  return dash(localizeUnitText(value, locale.value));
}
const fuelAverage = computed(() =>
  fuelDisplay(
    vehicle.value?.technical_details.fuel_consumption.combined_nedc.raw ??
      vehicle.value?.technical_details.fuel_consumption.combined_wltp.raw ??
      staticFuel('KYTUSEKULU_KESK'),
    aiFuelFigures.value?.combined,
  ),
);
const fuelCity = computed(() =>
  fuelDisplay(
    vehicle.value?.technical_details.fuel_consumption.city.raw ?? staticFuel('KYTUSEKULU_LINNAS'),
    aiFuelFigures.value?.city,
  ),
);
const fuelHighway = computed(() =>
  fuelDisplay(
    vehicle.value?.technical_details.fuel_consumption.highway.raw ?? staticFuel('KYTUSEKULU_TEEL'),
    aiFuelFigures.value?.highway,
  ),
);

// ---- tax card (veego) ----

const tax = computed(() =>
  props.session.tax.status === 'success' && props.session.tax.data ? extractCarTax(props.session.tax.data) : null,
);
const registrationTax = computed(() =>
  tax.value?.registrationTax !== null && tax.value?.registrationTax !== undefined
    ? formatEuro(Math.round(tax.value.registrationTax), locale.value)
    : null,
);
const annualTax = computed(() =>
  tax.value?.annualTax !== null && tax.value?.annualTax !== undefined
    ? formatEuro(Math.round(tax.value.annualTax), locale.value)
    : null,
);
const showTaxCard = computed(() => registrationTax.value !== null || annualTax.value !== null);

// Registration fee (from mnt) — whether it's been paid or is still due.
const registrationFeeStatus = computed(() => vehicle.value?.summary.registration_fee.status ?? null);
const registrationFeeUnpaid = computed(
  () => registrationFeeStatus.value === 'payable_on_next_transfer' || registrationFeeStatus.value === 'unpaid',
);
const registrationTaxLabel = computed(() => {
  const base = t('details.quick.registrationTax');
  if (registrationFeeUnpaid.value) return `${base} (${t('details.tax.unpaid')})`;
  if (registrationFeeStatus.value === 'paid') return `${base} (${t('details.tax.paid')})`;
  return base;
});

// ---- basic data card (mnt preferred, car-data until then) ----

function row(labelKey: string, value: string, tone?: DetailRow['tone']): DetailRow {
  const detail: DetailRow = { label: t(labelKey), value };
  if (tone) detail.tone = tone;
  return detail;
}

const basicRows = computed<DetailRow[]>(() => {
  if (vehicle.value) {
    const value = vehicle.value.identification.make_model?.trim();
    const [make, ...modelParts] = value ? value.split(/\s+/) : [];
    const summary = vehicle.value.summary;
    return [
      row('details.labels.make', dash(make ?? dyn.value?.make)),
      row('details.labels.model', dash(modelParts.join(' ') || dyn.value?.model)),
      row('details.labels.makeModel', dash(value)),
      row('details.labels.plate', registrationPlate.value),
      row('details.labels.vin', dash(vehicle.value.identification.vin)),
      row('details.labels.firstRegistration', dash(summary.first_registration.raw ?? summary.first_registration.iso)),
      row('details.labels.category', dash(translateCategory(summary.category, locale.value))),
      row('details.labels.bodyType', dash(translateBodyType(summary.body_type, locale.value))),
      row('details.labels.color', dash(translateColor(summary.body_color, locale.value))),
      row('details.labels.fuel', dash(translateFuelType(summary.fuel, locale.value))),
      row('details.labels.transmission', dash(translateTransmission(summary.transmission, locale.value))),
      row('details.labels.drive', dash(translateDriveType(summary.drive_type, locale.value))),
      row('details.labels.certificate', dash(summary.registration_certificate)),
      row(
        'details.labels.registrationFee',
        dash(translateRegistrationFee(summary.registration_fee.status, summary.registration_fee.raw, locale.value)),
        summary.registration_fee.status === 'payable_on_next_transfer' ? 'bad' : undefined,
      ),
    ];
  }
  if (latvianData.value) {
    const value = latvianData.value.make_model?.trim();
    const [make, ...modelParts] = value ? value.split(/\s+/) : [];
    return [
      row('details.labels.make', dash(latvianData.value.make ?? make)),
      row('details.labels.model', dash(latvianData.value.model ?? modelParts.join(' '))),
      row('details.labels.makeModel', dash(value)),
      row('details.labels.plate', registrationPlate.value),
      row('details.labels.validUntil', dash(latvianData.value.technical_inspection_until)),
      row('details.labels.type', dash(latvianData.value.vehicle_type)),
      row('details.labels.fuel', dash(latvianData.value.fuel_type)),
      row(
        'details.labels.inspectionType',
        dash(latvianData.value.status),
        latvianData.value.technical_inspection_valid === false
          ? 'bad'
          : latvianData.value.technical_inspection_valid === true
            ? 'good'
            : undefined,
      ),
    ];
  }
  if (dyn.value) {
    const lang = locale.value;
    return [
      row('details.labels.make', dash(dyn.value.make)),
      row('details.labels.model', dash(dyn.value.model)),
      row('details.labels.plate', registrationPlate.value),
      row('details.labels.firstRegistration', dash(stat.value?.ESMAREG_KP?.value)),
      row('details.labels.color', dash(translateColor(dyn.value.varv, lang))),
      row('details.labels.power', enginePower.value),
      row('details.labels.fuel', dash(translateFuelType(dyn.value.kutuse_tuup, lang))),
      row('details.labels.transmission', dash(translateTransmission(dyn.value.kaigukast, lang))),
      row('details.labels.bodyType', dash(translateBodyType(dyn.value.keretuup, lang))),
    ];
  }
  return [];
});

// car-data static fields fill the gap until (or in place of) the full /mnt
// document; each field carries its own display title from the API.
const staticRows = computed<DetailRow[]>(() => {
  if (vehicle.value || latvianData.value || !stat.value) return [];
  const labelKeys: Record<string, string> = {
    ESMAREG_KP: 'details.labels.firstRegistration',
    MOOTORI_MAHT: 'details.labels.displacement',
    TYHIMASS: 'details.labels.kerbWeight',
    TAISMASS: 'details.labels.grossWeight',
    KYTUSEKULU_KESK: 'details.quick.averageFuel',
    KYTUSEKULU_LINNAS: 'details.labels.fuelCity',
    KYTUSEKULU_TEEL: 'details.labels.fuelHighway',
    CO2: 'details.labels.co2Nedc',
  };
  return Object.entries(stat.value)
    .filter(
      (entry): entry is [string, { title: string; value: string }] =>
        Boolean(entry[1]?.title?.trim()) && Boolean(entry[1]?.value?.trim()),
    )
    .map(([key, field]) => {
      const descriptor = mntFieldTranslation(field.title);
      const labelKey = labelKeys[key] ?? descriptor.labelKey;
      const translatedValue = translateMntValue(descriptor.kind, field.value, locale.value);
      return {
        label: labelKey ? t(labelKey) : field.title,
        value:
          formatStaticValue(translatedValue, locale.value) ??
          translateGenericValue(field.value, locale.value) ??
          field.value,
      };
    });
});
</script>

<template>
  <div class="details-root">
    <main class="details-scroll">
      <!-- Tap-to-explain: every quick-card cell below is a button that scrolls
           to (and pulses) the full section it summarizes — see useSectionPulse. -->
      <section class="vehicle-hero">
        <h1>
          <button type="button" class="hero-tap" @click="scrollToSection(['sec-basic'], $event)">
            {{ title }}
          </button>
        </h1>
      </section>

      <section
        v-if="latvianShortcut && latvianPending"
        class="quick-card card-loading"
        :aria-label="$t('details.quick.vehicleSummary')"
      >
        <span class="spinner small-spinner" aria-hidden="true"></span>
        <span>{{ $t('details.loadingRegistry') }}</span>
      </section>

      <section
        v-else-if="latvianShortcut && latvianData"
        class="quick-card quick-status-card"
        :aria-label="$t('details.quick.vehicleSummary')"
      >
        <div class="quick-status-grid quick-status-primary">
          <button
            type="button"
            class="quick-stat tap-stat"
            :class="{ good: latvianData.technical_inspection_valid === true, bad: latvianData.technical_inspection_valid === false }"
            @click="scrollToSection(['sec-basic'], $event)"
          >
            <QuickIcon name="inspection" class="quick-icon" />
            <strong>{{ dash(latvianData.status) }}</strong>
          </button>
          <button type="button" class="quick-stat tap-stat" @click="scrollToSection(['sec-basic'], $event)">
            <QuickIcon name="speedometer" class="quick-icon" />
            <strong>{{ dash(latvianData.technical_inspection_until) }}</strong>
          </button>
          <button type="button" class="quick-stat tap-stat" @click="scrollToSection(['sec-basic'], $event)">
            <QuickIcon name="person" class="quick-icon" />
            <strong>{{ dash(latvianData.vehicle_type) }}</strong>
          </button>
          <button type="button" class="quick-stat tap-stat" @click="scrollToSection(['sec-basic'], $event)">
            <QuickIcon name="pledge" class="quick-icon" />
            <strong>{{ dash(latvianData.fuel_type) }}</strong>
          </button>
        </div>
      </section>

      <section
        v-else
        class="quick-card quick-status-card"
        :class="{ degraded: session.mnt.status === 'error' || mntNotFound }"
        :aria-label="$t('details.quick.vehicleSummary')"
        :aria-busy="mntPending"
      >
        <div class="quick-status-grid quick-status-primary">
          <button
            type="button"
            class="quick-stat tap-stat"
            :aria-label="`${$t('details.compact.owner')}: ${ownerTypeLabel}`"
            @click="scrollToSection(['sec-status', 'sec-operations'], $event)"
          >
            <QuickIcon :name="isCorporate ? 'company' : 'person'" class="quick-icon" />
            <strong>{{ ownerTypeLabel }}</strong>
          </button>
          <button
            type="button"
            class="quick-stat tap-stat"
            :class="{ bad: vehicle?.mileage.summary.has_rollback }"
            :aria-label="$t('details.compact.mileage')"
            @click="scrollToSection(['sec-mileage'], $event)"
          >
            <QuickIcon name="speedometer" class="quick-icon quick-icon--lg" />
            <strong>{{ mileageSummary }}</strong>
          </button>
          <button
            type="button"
            class="quick-stat tap-stat"
            :class="inspectionTone"
            :aria-label="$t('details.compact.inspection')"
            @click="scrollToSection(['sec-inspections'], $event)"
          >
            <QuickIcon name="inspection" class="quick-icon" />
            <strong>{{ inspectionDate }}</strong>
          </button>
          <button
            type="button"
            class="quick-stat tap-stat"
            :class="pledgeTone"
            :aria-label="$t('details.compact.pledge')"
            @click="scrollToSection(['sec-restrictions'], $event)"
          >
            <QuickIcon name="pledge" class="quick-icon" />
            <strong>{{ pledgeSummary }}</strong>
          </button>
        </div>
        <div class="quick-divider"></div>
        <div class="quick-status-grid quick-status-secondary">
          <button
            type="button"
            class="quick-stat country-stat tap-stat"
            :aria-label="$t('details.compact.origin')"
            @click="scrollToSection(['sec-registration'], $event)"
          >
            <span class="country-flag" aria-hidden="true">{{ currentCountryFlag }}</span>
          </button>
          <button
            type="button"
            class="quick-stat owner-duration-stat tap-stat"
            @click="scrollToSection(['sec-operations'], $event)"
          >
            <span>{{ $t('details.compact.duration') }}</span>
            <strong>{{ currentOwnerDuration }}</strong>
          </button>
          <button
            type="button"
            class="quick-stat owner-count-stat tap-stat"
            @click="scrollToSection(['sec-operations'], $event)"
          >
            <span>{{ $t('details.compact.ownerCount') }}</span>
            <strong><QuickIcon name="owners" class="owner-count-icon" /> {{ currentOwnerCount }}</strong>
          </button>
        </div>
      </section>

      <section
        v-if="!latvianShortcut && session.price.status === 'loading'"
        class="quick-card quick-price-card card-loading"
        :aria-label="$t('details.quick.averagePrice')"
      >
        <span class="spinner small-spinner" aria-hidden="true"></span>
        <span>{{ $t('details.price.loading') }}</span>
      </section>
      <section
        v-else-if="!latvianShortcut && priceRange"
        class="quick-card quick-price-card"
        :aria-label="$t('details.quick.averagePrice')"
        role="button"
        tabindex="0"
        @click="revealPriceCaption"
        @keydown.enter.prevent="revealPriceCaption"
        @keydown.space.prevent="revealPriceCaption"
      >
        <div class="price-badge-icon" aria-hidden="true"><QuickIcon name="price" /></div>
        <strong>{{ priceRange.low }}</strong>
        <template v-if="priceRange.high">
          <span>{{ $t('details.price.separator') }}</span>
          <strong>{{ priceRange.high }}</strong>
        </template>
        <p v-if="priceCaptionVisible" class="price-caption">{{ $t('details.quick.averagePrice') }}</p>
      </section>

      <section v-if="!latvianShortcut" class="quick-card quick-performance-card" :aria-label="$t('details.sections.performance')">
        <div class="quick-performance-grid">
          <button
            type="button"
            class="quick-performance-cell power-cell tap-stat"
            @click="scrollToSection(['sec-engine'], $event)"
          >
            <strong>{{ enginePower }}</strong>
            <span>{{ engineDisplacementLabel }}</span>
            <span v-if="fuelTypeLabel" class="power-fuel">{{ fuelTypeLabel }}</span>
          </button>
          <button
            type="button"
            class="quick-performance-cell tap-stat"
            @click="scrollToSection(['sec-performance'], $event)"
          >
            <span>{{ $t('details.quick.acceleration') }}</span>
            <strong>{{ acceleration }}</strong>
            <small v-if="acceleration100200" class="accel-sub">
              {{ $t('details.quick.acceleration100200') }} ·
              <span class="accel-sub-value">{{ acceleration100200 }}</span>
            </small>
          </button>
          <button
            type="button"
            class="quick-performance-cell tap-stat"
            @click="scrollToSection(['sec-masses'], $event)"
          >
            <span>{{ $t('details.labels.kerbWeight') }}</span>
            <strong>{{ kerbWeight }}</strong>
          </button>
        </div>
        <div class="quick-divider"></div>
        <div class="quick-performance-grid quick-fuel-grid">
          <button
            type="button"
            class="quick-performance-cell tap-stat"
            @click="scrollToSection(['sec-performance'], $event)"
          >
            <span>{{ $t('details.quick.averageFuel') }}</span>
            <strong>{{ fuelAverage }}</strong>
          </button>
          <button
            type="button"
            class="quick-performance-cell tap-stat"
            @click="scrollToSection(['sec-performance'], $event)"
          >
            <span>{{ $t('details.labels.fuelCity') }}</span>
            <strong>{{ fuelCity }}</strong>
          </button>
          <button
            type="button"
            class="quick-performance-cell tap-stat"
            @click="scrollToSection(['sec-performance'], $event)"
          >
            <span>{{ $t('details.labels.fuelHighway') }}</span>
            <strong>{{ fuelHighway }}</strong>
          </button>
        </div>
      </section>

      <section v-if="!latvianShortcut && showTaxCard" class="quick-card quick-tax-card" :aria-label="$t('details.quick.taxInfo')">
        <button type="button" class="tap-stat" @click="scrollToSection(['sec-basic'], $event)">
          <span>{{ registrationTaxLabel }}</span>
          <strong :class="{ bad: registrationFeeUnpaid, good: registrationFeeStatus === 'paid' }">{{ dash(registrationTax) }}</strong>
        </button>
        <!-- Annual tax has no deeper section — the tap pulses the cell itself. -->
        <button type="button" class="tap-stat" @click="scrollToSection([], $event)">
          <span>{{ $t('details.quick.annualTax') }}</span>
          <strong>{{ dash(annualTax) }}</strong>
        </button>
      </section>

      <section
        v-if="!latvianShortcut && session.ai.status === 'loading'"
        class="quick-card ai-summary-card card-loading"
        :aria-label="$t('details.quick.aiSummary')"
      >
        <span class="spinner small-spinner" aria-hidden="true"></span>
        <span>{{ $t('details.ai.loading') }}</span>
      </section>
      <section
        v-else-if="!latvianShortcut && session.ai.status === 'success' && session.ai.data"
        class="quick-card ai-summary-card"
        :class="{ expanded: aiExpanded }"
        :aria-label="$t('details.quick.aiSummary')"
        :aria-expanded="aiExpanded"
        role="button"
        tabindex="0"
        @click="aiExpanded = !aiExpanded"
        @keydown.enter.prevent="aiExpanded = !aiExpanded"
        @keydown.space.prevent="aiExpanded = !aiExpanded"
      >
        <p>{{ session.ai.data.description }}</p>
      </section>
      <section
        v-else-if="!latvianShortcut && session.ai.status === 'error'"
        class="quick-card ai-summary-card"
        :aria-label="$t('details.quick.aiSummary')"
      >
        <p class="muted">{{ $t('details.ai.unavailable') }}</p>
      </section>

      <section v-if="basicRows.length > 0" id="sec-basic" class="detail-card">
        <h2>{{ $t('details.sections.basic') }}</h2>
        <dl class="details-list">
          <template v-for="item in basicRows" :key="item.label">
            <dt>{{ item.label }}</dt>
            <dd :class="item.tone">{{ item.value }}</dd>
          </template>
        </dl>
      </section>

      <section v-if="staticRows.length > 0" class="detail-card">
        <h2>{{ $t('details.sections.other') }}</h2>
        <dl class="details-list">
          <template v-for="item in staticRows" :key="item.label">
            <dt>{{ item.label }}</dt>
            <dd>{{ item.value }}</dd>
          </template>
        </dl>
      </section>

      <section v-if="latvianShortcut && latvianError" class="detail-card mnt-error-card">
        <h2>{{ $t('details.registryError') }}</h2>
        <p class="muted">{{ session.latvian.error }}</p>
      </section>

      <section v-else-if="!latvianShortcut && mntNotFound" class="empty-details">
        <h2>{{ $t('details.notFoundTitle') }}</h2>
        <p class="muted">{{ $t('details.notFoundBody') }}</p>
      </section>

      <section v-else-if="!latvianShortcut && mntPending" class="detail-card card-loading mnt-pending-card">
        <span class="spinner small-spinner" aria-hidden="true"></span>
        <span>{{ $t('details.loadingRegistry') }}</span>
      </section>

      <section v-else-if="!latvianShortcut && session.mnt.status === 'error'" class="detail-card mnt-error-card">
        <h2>{{ $t('details.registryError') }}</h2>
        <p class="muted">{{ session.mnt.error }}</p>
        <button class="btn" @click="session.retryMnt()">{{ $t('lookup.retry') }}</button>
      </section>

      <MntDetailSections v-else-if="!latvianShortcut && mntResponse" :response="mntResponse" />
    </main>

    <button class="fab-menu" :aria-label="$t('aria.history')" @click="showHistory = true">
      ≡
    </button>

    <button class="fab-abc" :aria-label="$t('aria.textSearch')" @click="showSearch = true">
      ABC
    </button>

    <button class="fab-scan fab-scan--sm" :aria-label="$t('details.newScan')" @click="$emit('back')">
      <QuickIcon name="camera" />
    </button>

    <HistoryPanel v-if="showHistory" @close="showHistory = false" @open="openFromHistory" />

    <div v-if="showSearch" class="panel-backdrop" @click.self="showSearch = false">
      <div class="history-panel search-sheet">
        <div class="panel-head">
          <h2>{{ $t('manual.label') }}</h2>
          <button class="icon-btn" :aria-label="$t('aria.close')" @click="showSearch = false">✕</button>
        </div>
        <ManualLookupForm :busy="false" @lookup="openLookup" />
      </div>
    </div>
  </div>
</template>
